import { useCallback } from 'react';
import { runNestTasksAction } from './runNestTasksAction';
import { runCompileMissionsIntoNestedCampaign } from './runCompileMissionsIntoNestedCampaign';
import { runExplodeNestedCampaign } from './runExplodeNestedCampaign';

/**
 * useNestTasks Hook
 *
 * Handles nesting tasks into another task's nested campaign.
 * Supports three strategies: break, patch, chain.
 *
 * UNDO SUPPORT:
 * - Records the entire nest operation as a single undoable action
 * - If a nested campaign was created, it will be trashed on undo
 * - Restores tasks to parent sprint with their original positions and dependencies
 */
export const useNestTasks = ({
  tasks,
  localSprint,
  setLocalSprint,
  nestedSprints,
  allSprints,
  onAddSprint,
  onUpdateSprint,
  recordAction,
  onTrashSprint,
  flowchartRef,
  loadFullSprint,
}) => {
  const handleNestTasks = useCallback(
    (nestAction) => {
      runNestTasksAction({
        nestAction,
        tasks,
        nestedSprints,
        localSprint,
        setLocalSprint,
        onAddSprint,
        onUpdateSprint,
        recordAction,
        onTrashSprint,
      });
    },
    [tasks, nestedSprints, localSprint, setLocalSprint, onAddSprint, onUpdateSprint, recordAction, onTrashSprint],
  );

  const handleCompileMissionsIntoNestedCampaign = useCallback(
    ({ missionName, draggedIds }) => {
      runCompileMissionsIntoNestedCampaign({
        missionName,
        draggedIds,
        tasks,
        nestedSprints,
        allSprints,
        localSprint,
        setLocalSprint,
        onAddSprint,
        onUpdateSprint,
        recordAction,
        onTrashSprint,
        flowchartRef,
      });
    },
    [tasks, nestedSprints, allSprints, localSprint, setLocalSprint, onAddSprint, onUpdateSprint, recordAction, onTrashSprint, flowchartRef],
  );

  const handleExplodeNestedCampaign = useCallback(
    (wrapperTaskId) => {
      runExplodeNestedCampaign({
        wrapperTaskId,
        tasks,
        nestedSprints,
        allSprints,
        localSprint,
        setLocalSprint,
        onAddSprint,
        onUpdateSprint,
        recordAction,
        onTrashSprint,
        flowchartRef,
        loadFullSprint,
      });
    },
    [tasks, nestedSprints, allSprints, localSprint, setLocalSprint, onAddSprint, onUpdateSprint, recordAction, onTrashSprint, flowchartRef, loadFullSprint],
  );

  return { handleNestTasks, handleCompileMissionsIntoNestedCampaign, handleExplodeNestedCampaign };
};

export default useNestTasks;
